import CloseIcon from "@mui/icons-material/CloseRounded";
import { Box, Divider, IconButton, Stack, Typography } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type { Report, ReportTemplate } from "../../../../../api/types/svTypes";
import { ReportType } from "../../../../../api/types/svTypes";
import EditableTitle from "../../../../common/EditableTitle";
import GlobalDndProvider from "../../../../common/GlobalDndProvider";
import ChartEditor from "../../../reporting/sv/components/builder/chart/ChartEditor";
import { ExportStateContextProvider } from "../../../reporting/sv/components/builder/common/contexts/ExportStateContextProvider";
import CustomDragLayer from "../../../reporting/sv/components/builder/common/dragLayer/CustomDragLayer";
import FieldsComponent from "../../../reporting/sv/components/builder/common/fields/Fields";
import PivotEditor from "../../../reporting/sv/components/builder/pivot/PivotEditor";
import ReportEditor from "../../../reporting/sv/components/builder/ReportEditor";
import TabularEditor from "../../../reporting/sv/components/builder/tabular/TabularEditor";
import TileEditor from "../../../reporting/sv/components/builder/tile/TileEditor";
import CollapseContainer from "../../../reporting/sv/components/common/CollapseContainer";
import EntityLevelAccess from "../../../reporting/sv/components/common/EntityLevelAccess";
import PartContainer from "../../../reporting/sv/components/viewer/common/PartContainer";
import {
  selectCurrentReport,
  selectCurrentReportType,
  selectResetId,
} from "../../../reporting/sv/store/selectors/currentReportSelectors";
import { isChartReportType } from "../../../reporting/sv/utilities/reportTypeHelper";
import { useCurrentTileContext } from "../../contexts/CurrentTileContext";
import { useDashboardContext } from "../../contexts/DashboardContext";
import SaveButton from "./SaveButton";

export default function EditReportPage({ widgetId }: { widgetId: string }) {
  const { hasAnyPermission } = useClientPermissionsContext();
  const report = useSelector(selectCurrentReport);
  const reportType = useSelector(selectCurrentReportType);
  const resetId = useSelector(selectResetId);

  const chartReport = React.useMemo(() => isChartReportType(report?.reportType), [report]);

  if (report === undefined) return <></>;

  if (!hasAnyPermission(["ManageReports"])) {
    return <Navigate to={".."} />;
  }

  return (
    <ExportStateContextProvider>
      <GlobalDndProvider>
        <Stack sx={{ flex: 1 }}>
          <ReportActionHeader report={report} widgetId={widgetId} />
          <Divider />
          <Box sx={{ display: "flex", flex: 1 }}>
            <CollapseContainer
              expandCollapsePaddingTop={25}
              collapseElement={
                <Typography
                  variant="h6"
                  sx={(theme) => ({ color: theme.palette.text.primary, transform: "rotate(90deg)" })}
                >
                  Fields
                </Typography>
              }
            >
              <PartContainer sx={{ maxWidth: "21rem", minWidth: "21rem" }} caption={<Box></Box>}>
                <FieldsComponent />
              </PartContainer>
            </CollapseContainer>
            <React.Fragment key={resetId}>
              {(!reportType || reportType === ReportType.Pivot) && (
                <ReportEditor report={report} Editor={PivotEditor} />
              )}
              {reportType === ReportType.Tabular && <ReportEditor report={report} Editor={TabularEditor} />}
              {chartReport && <ReportEditor report={report} Editor={ChartEditor} />}
              {reportType === ReportType.Tile && <ReportEditor report={report} Editor={TileEditor} />}
            </React.Fragment>
          </Box>
        </Stack>
        <CustomDragLayer />
      </GlobalDndProvider>
    </ExportStateContextProvider>
  );
}

const ReportActionHeader = ({ report, widgetId }: { report: Report | ReportTemplate; widgetId: string }) => {
  const navigate = useNavigate();
  const { tile } = useCurrentTileContext();
  const { updateTileWidget } = useDashboardContext();

  const onTitleChanged = (title: string) => {
    updateTileWidget(widgetId, { nameUserInput: title });
  };

  const onSaved = () => {
    updateTileWidget(widgetId, {
      etag: generateGuid(),
    });
  };

  return (
    <Box
      sx={(t) => ({
        display: "flex",
        px: 3,
        py: 1.25,
        alignItems: "center",
        gap: 2,
        maxHeight: t.spacing(7.5),
        height: t.spacing(6.5),
      })}
    >
      {/* {!isRenaming && (
        <Typography color="text.primary" variant="h6" noWrap>
          {report.name}
        </Typography>
      )} */}
      <EditableTitle
        title={tile.item.widget.nameUserInput}
        tooltip="Rename Widget"
        onTitleChanged={onTitleChanged}
        placeholder="Enter Widget Name"
      />
      <HorizontalFill />
      <Box sx={{ flexGrow: 1 }}></Box>
      <Box>
        <Stack direction={"row"} sx={{ alignItems: "center", gap: 1 }}>
          <EntityLevelAccess />
          <SaveButton report={report} onSaved={onSaved} />
          <IconButton onClick={() => navigate(`..`)}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
};
